import type { ApiConfigCategory, ImageProvider } from "@foto/shared";
import type { SaidaRota } from "../../lib/trpc.js";

export type ConfigDaLista = SaidaRota["config"]["list"][number];

/**
 * As seções de configuração, uma por endereço.
 *
 * Cada seção é uma tela. Uma página só com as vinte e nove chaves obrigava a
 * rolar procurando pelo nome, e as chaves mortas da migração se misturavam às
 * vivas.
 */
export interface Secao {
  /** Vai na URL: /admin/config/<slug> */
  slug: string;
  titulo: string;
  sobre: string;
  /**
   * A categoria que esta seção mostra. `null` é a seção das chaves que este
   * sistema não declara — elas não têm categoria própria, e sim a que herdaram.
   */
  categoria: ApiConfigCategory | null;
}

export const SECOES: Secao[] = [
  {
    slug: "imagem",
    titulo: "Geração de imagem",
    sobre: "O modelo que cria a peça e a resolução do arquivo produzido.",
    categoria: "image",
  },
  {
    slug: "openrouter",
    titulo: "OpenRouter",
    sobre: "Credencial e modelo de texto usado nas sugestões de prompt.",
    categoria: "general",
  },
  {
    slug: "fal",
    titulo: "fal.ai",
    sobre: "Credencial e modelo do segundo provedor de imagem.",
    categoria: "fal",
  },
  {
    slug: "custo",
    titulo: "Estimativa de custo",
    sobre: "Quanto cada chamada de IA custou, calculado por um modelo à parte.",
    categoria: "cost",
  },
  {
    slug: "bunny",
    titulo: "Bunny",
    sobre: "Onde os arquivos ficam e como as imagens são assinadas para exibição.",
    categoria: "storage",
  },
  {
    slug: "whatsapp",
    titulo: "WhatsApp",
    sobre: "Entrega da foto à pessoa pelo WhatsApp da SER (WAHA), uma mensagem por vez, pela fila.",
    categoria: "whatsapp",
  },
  {
    slug: "entrada",
    titulo: "Entrada",
    sobre: "Login com Google e criação de conta.",
    categoria: "auth",
  },
  {
    slug: "email",
    titulo: "E-mail",
    sobre: "Por onde saem recibos e avisos, pelo Mailtrap.",
    categoria: "email",
  },
  {
    slug: "pagamento",
    titulo: "Pagamento",
    sobre: "Cobrança dos pacotes de fotos pelo Mercado Pago.",
    categoria: "pagamento",
  },
  {
    slug: "galeria",
    titulo: "Galeria",
    sobre: "Ritmo da exibição na TV durante a vivência.",
    categoria: "galeria",
  },
  {
    slug: "landing",
    titulo: "Landing",
    sobre: "Para onde vão os pedidos de demonstração e o contato comercial público.",
    categoria: "landing",
  },
  {
    slug: "heranca",
    titulo: "Herança da migração",
    sobre: "Chaves que vieram do sistema de vídeo e que nenhum código lê. Podem ser removidas.",
    categoria: null,
  },
];

/** As chaves de uma seção. A de herança pega tudo que o sistema não declara. */
export function chavesDa(secao: Secao, todas: ConfigDaLista[]): ConfigDaLista[] {
  return secao.categoria === null
    ? todas.filter((c) => !c.conhecida)
    : todas.filter((c) => c.conhecida && c.category === secao.categoria);
}

/**
 * Chaves que são modelo de IA — define quem ganha o seletor, e de qual catálogo.
 *
 * O provedor faz parte da identidade da chave: `fal_image_model` e
 * `openrouter_image_model` são os dois de imagem, e nenhum dos dois catálogos
 * serve para o outro.
 */
export const MODALIDADE: Record<
  string,
  { modalidade: "imagem" | "texto" | "visao"; provedor: ImageProvider }
> = {
  openrouter_image_model: { modalidade: "imagem", provedor: "openrouter" },
  openrouter_vision_model: { modalidade: "visao", provedor: "openrouter" },
  fal_image_model: { modalidade: "imagem", provedor: "fal" },
  openrouter_text_model: { modalidade: "texto", provedor: "openrouter" },
  cost_estimator_model: { modalidade: "texto", provedor: "openrouter" },
};

/**
 * Nome de cada campo para quem opera o sistema.
 *
 * A chave (`galeria_photo_display_seconds`) é nome de coluna: serve para quem
 * abre o banco ou lê a documentação, e não diz nada a quem está configurando. Ela
 * continua visível, discreta, ao lado do nome — mas quem manda no rótulo é esta
 * tabela.
 *
 * Sem entrada aqui, cai na descrição do banco e, por último, na própria chave.
 * Chave nova nunca fica sem rótulo.
 */
export const ROTULOS: Record<string, string> = {
  app_base_url: "Endereço público do sistema",
  cadastro_fotos_cortesia: "Fotos de cortesia por cadastro",
  google_client_id: "Client ID do Google",
  google_client_secret: "Client secret do Google",
  mailtrap_api_token: "Token da API",
  mailtrap_remetente: "Remetente",
  mailtrap_remetente_nome: "Nome do remetente",
  mailtrap_sandbox_enabled: "Modo de teste",
  mailtrap_test_inbox_id: "Caixa de teste",
  mercadopago_access_token: "Access token",
  mercadopago_public_key: "Public key",
  mercadopago_webhook_secret: "Segredo do webhook",
  landing_email_comercial: "E-mail comercial",
  landing_whatsapp_url: "Link do WhatsApp comercial",
  openrouter_management_key: "Chave de gerenciamento",
  openrouter_api_url: "Endereço da API",
  openrouter_api_key: "Chave de acesso",
  image_provider: "Quem gera a imagem",
  openrouter_image_model: "Modelo que gera a imagem",
  fal_api_url: "Endereço da fila",
  fal_api_key: "Chave de acesso",
  fal_image_model: "Modelo que gera a imagem",
  fal_image_size: "Proporção da imagem",
  fal_rendering_speed: "Modo de renderização",
  openrouter_image_resolution: "Resolução da imagem",
  openrouter_vision_model: "Modelo que analisa a foto do lote",
  openrouter_text_model: "Modelo que sugere o prompt",
  ai_image_prompt_base: "Instrução para a sugestão de prompt",
  bunny_storage_zone: "Nome da storage zone",
  bunny_storage_api_key: "Senha da storage zone",
  bunny_storage_region: "Região do storage",
  bunny_pull_zone_url: "Endereço público das imagens",
  bunny_zone_security_key: "Chave que assina as imagens",
  waha_url: "Endereço do WAHA",
  waha_api_key: "Chave da API",
  galeria_photo_display_seconds: "Tempo de cada foto",
  galeria_divulgacao_every: "Divulgação a cada N fotos",
  cost_estimator_enabled: "Estimar custo automaticamente",
  cost_estimator_model: "Modelo que estima o custo",
  crm_api_key: "Chave de acesso do CRM",
  whatsapp_mensagem_1: "Mensagem 1 (sorteada a cada envio)",
  whatsapp_mensagem_2: "Mensagem 2 (sorteada a cada envio)",
  whatsapp_mensagem_3: "Mensagem 3 (sorteada a cada envio)",
  whatsapp_mensagem_4: "Mensagem 4 (sorteada a cada envio)",
  agente_diretor_ativo: "Diretor de IA nos marcadores",
  agente_paleta_da_capa: "Cores da capa nos detalhes (experimental)",
  agente_luz_especifica: "Luz detalhada em cada estilo (experimental)",
};

export function rotuloDe(config: ConfigDaLista): string {
  return ROTULOS[config.key] ?? config.description ?? config.key;
}

/**
 * Explicação de uma chave, em linguagem de quem administra.
 *
 * Só as do agente de prompts têm texto aqui: elas mudam o que a IA recebe, e quem liga ou
 * desliga precisa saber o que vai mudar na imagem. As demais seguem com a descrição do banco,
 * que é onde o seed a declara.
 */
export const DICAS: Record<string, string> = {
  waha_api_key:
    "A chave deste sistema no WAHA da SER, entregue pelo responsável pelo WhatsApp. Ela só lê o estado e envia mensagens. Depois de salva, não aparece mais na tela.",
  whatsapp_mensagem_1:
    "Quem recebe pode responder, e a resposta cai no atendimento da SER: diga de onde vem a foto (o nome do evento ou da casa).",
  agente_diretor_ativo:
    "Ligado, quando o operador usa o modo Automático dos marcadores, a IA escolhe três estilos que combinam com o texto e cria o cenário e o ornamento do tema. Desligado, ela volta a escrever três direções livres, como antes. Se a IA falhar, o conjunto sai com os três estilos padrão, sem erro na tela.",
  agente_paleta_da_capa:
    "Experimental. Ligado, as cores dominantes da capa do livro, tiradas por conta e sem IA, entram como detalhe de cor do título e dos ornamentos. Só vale com capa escolhida e com estilos do catálogo. No ensaio de 02/10 não melhorou o resultado e chegou a pintar uma barra de cor no rodapé: compare antes de deixar ligado.",
  agente_luz_especifica:
    "Experimental. Ligado, cada estilo descreve a luz com fonte, direção e temperatura (por exemplo, sol baixo vindo do fundo, quente), para as pessoas combinarem com a cena. Só vale com estilos do catálogo. No ensaio de 02/10 melhorou a luz nas pessoas numa amostra e piorou noutra: compare antes de deixar ligado.",
};

export function dicaDe(config: ConfigDaLista): string | undefined {
  return DICAS[config.key] ?? config.description ?? undefined;
}

/**
 * Chaves cujo valor vem de uma lista fechada.
 *
 * Digitar à mão um valor que a API só aceita em quatro formas é convite a erro —
 * e a falha aparece bem depois, na hora de gerar. Fora da lista continua sendo
 * possível pela opção "Outro", porque a lista aqui é uma fotografia do que o
 * provedor aceita hoje.
 */
export const OPCOES: Record<string, { valor: string; rotulo: string }[]> = {
  image_provider: [
    { valor: "openrouter", rotulo: "OpenRouter — modelo em Geração de imagem" },
    { valor: "fal", rotulo: "fal.ai — modelo na seção fal.ai" },
  ],
  // Enum do `image_size` do fal. Vertical é o formato da peça: a capa da
  // vivência ocupa os 40% de cima e o retrato os 60% de baixo.
  fal_image_size: [
    { valor: "portrait_4_3", rotulo: "portrait_4_3 — vertical, o formato da peça" },
    { valor: "portrait_16_9", rotulo: "portrait_16_9 — vertical alongado" },
    { valor: "square_hd", rotulo: "square_hd — quadrado em alta" },
    { valor: "square", rotulo: "square — quadrado" },
    { valor: "landscape_4_3", rotulo: "landscape_4_3 — deitado" },
    { valor: "landscape_16_9", rotulo: "landscape_16_9 — deitado alongado" },
  ],
  // Só os modelos Ideogram usam; nos demais o campo é ignorado pelo serviço.
  fal_rendering_speed: [
    { valor: "TURBO", rotulo: "TURBO — mais barato e mais rápido" },
    { valor: "BALANCED", rotulo: "BALANCED — o meio-termo" },
    { valor: "QUALITY", rotulo: "QUALITY — mais caro, melhor acabamento" },
  ],
  // Documentação do OpenRouter (POST /api/v1/images): resolution aceita apenas
  // estes quatro níveis. Não existem intermediários.
  openrouter_image_resolution: [
    { valor: "512", rotulo: "512 — rascunho, mais barato" },
    { valor: "1K", rotulo: "1K — suficiente para tela" },
    { valor: "2K", rotulo: "2K — padrão atual" },
    { valor: "4K", rotulo: "4K — máxima, arquivo bem maior" },
  ],
  bunny_storage_region: [
    { valor: "br", rotulo: "br — São Paulo" },
    { valor: "de", rotulo: "de — Frankfurt" },
    { valor: "ny", rotulo: "ny — Nova York" },
    { valor: "la", rotulo: "la — Los Angeles" },
    { valor: "uk", rotulo: "uk — Londres" },
    { valor: "se", rotulo: "se — Estocolmo" },
    { valor: "sg", rotulo: "sg — Singapura" },
    { valor: "syd", rotulo: "syd — Sydney" },
    { valor: "jh", rotulo: "jh — Joanesburgo" },
  ],
};

/**
 * Prompts e templates são textos de vários parágrafos.
 *
 * A decisão vem do nome da chave, e não do tamanho do valor: pelo tamanho, uma
 * chave de prompt ainda vazia nasceria como campo de uma linha e mudaria de
 * forma conforme fosse digitada. O nome é estável e o seed é quem o define, então
 * um prompt novo já nasce com o campo certo.
 */
export function ehTextoLongo(chave: string): boolean {
  return /prompt|template|instruc|mensagem/i.test(chave);
}

/**
 * Liga/desliga.
 *
 * Vale tanto pelo nome quanto pelo valor guardado: o nome cobre a chave que
 * ainda está vazia, e o valor cobre uma booleana cujo nome não segue a
 * convenção. `"true"` e `"false"` em texto é como o banco guarda — a coluna é
 * `text`, sem tipo booleano.
 */
export function ehBooleana(config: ConfigDaLista): boolean {
  return (
    /_enabled$|^enable_/.test(config.key) ||
    CHAVES_LIGA_DESLIGA.has(config.key) ||
    config.value === "true" ||
    config.value === "false"
  );
}

/**
 * Interruptores cujo nome não segue a convenção `_enabled`. Sem estar aqui, um valor apagado
 * viraria um campo de texto no lugar do interruptor.
 */
const CHAVES_LIGA_DESLIGA = new Set([
  "agente_diretor_ativo",
  "agente_paleta_da_capa",
  "agente_luz_especifica",
]);

/** Interruptores que valem ligados quando o valor está vazio: é o que o servidor assume. */
const LIGADAS_POR_PADRAO = new Set(["agente_diretor_ativo"]);

/**
 * O interruptor aparece ligado?
 *
 * Valor vazio não é "desligado" para todas as chaves: o servidor lê o vazio de
 * `agente_diretor_ativo` como ligado, e o interruptor mostrando o contrário contaria uma
 * mentira ao administrador.
 */
export function estaLigada(chave: string, valor: string): boolean {
  // As demais chaves guardam exatamente "true" ou "false", e assim continuam sendo lidas.
  if (!CHAVES_LIGA_DESLIGA.has(chave)) return valor === "true";
  const v = valor.trim().toLowerCase();
  return v === "" ? LIGADAS_POR_PADRAO.has(chave) : v === "true";
}
